import { field } from './guideSearch'
import type { SearchDoc } from './guideSearch'
import { ACCESS_PENDING_LABEL, accessLabel, coordLabel, documentedAccessLevels, itemName, itemPath, mapPath, monsterPath, prettyAccessName, spotPath } from './worldFormat'
import { skillKindOf, skillNameOf } from './skillItems'
import { spanishAliasesFor } from '../data/guide/itemTranslations'
import { npcs } from '../data/guide/npcs'
import { npcDoc } from './progressionSearch'
import { npcLocationText, resolveNpcLocation } from './npcLocation'
import { publishedShopItems, shopOf } from './npcShops'
import { monsterKind } from './monsterKind'
import { canonicalNpcId, npcDisplayName, npcIdentity } from '../data/guide/npcIdentity'
import { npcs as guideNpcList } from '../data/guide/npcs'

const guideNpcName = (monsterId: number) => guideNpcList.find((n) => n.monsterId === monsterId)?.name
import type { WorldData } from '../types/world'

/** Documentos de búsqueda construidos a partir de los datos importados. */
export function buildWorldDocs(w: WorldData): SearchDoc[] {
  const docs: SearchDoc[] = []

  w.maps.forEach((m) => {
    const levels = documentedAccessLevels(m)
    docs.push({
      key: `map-${m.id}`,
      groupKey: `map-${m.id}`,
      group: 'Mapas',
      title: m.name,
      typeLabel: 'Mapa',
      context: accessLabel(m),
      path: mapPath(m.id),
      fields: [
        field(m.name, 10, 'Nombre del mapa'),
        ...m.moveEntries.map((e) => field(prettyAccessName(e.name), 7, 'Acceso')),
        field('mapa spots ubicacion', 2, 'Mapa'),
      ],
    })
    // Acceso y requisitos de CADA mapa (también sin /move): destino de "cómo entro a X".
    docs.push({
      key: `map-${m.id}-accesos`,
      groupKey: `map-${m.id}`,
      group: 'Mapas',
      title: `Accesos y requisitos de ${m.name}`,
      typeLabel: 'Acceso al mapa',
      context: levels.length ? `Acceso desde nivel ${Math.min(...levels)}` : `${ACCESS_PENDING_LABEL} · sin /move documentado`,
      path: `${mapPath(m.id)}#accesos`,
      fields: [field(m.name, 10, 'Mapa'), field('acceso accesos entrada entrar requisitos requisito nivel', 4, 'Acceso')],
    })
    m.moveEntries.forEach((e, i) => {
      if (e.minLevel === undefined) return
      docs.push({
        key: `map-${m.id}-acc-${i}`,
        groupKey: `map-${m.id}`,
        group: 'Mapas',
        title: `${prettyAccessName(e.name)} — nivel ${e.minLevel}`,
        typeLabel: 'Acceso al mapa',
        context: m.name,
        path: `${mapPath(m.id)}#accesos`,
        fields: [field(prettyAccessName(e.name), 10, 'Acceso'), field(m.name, 3, 'Mapa'), field(`nivel requisito acceso entrada ${e.minLevel}`, 3, 'Requisito')],
      })
    })
  })

  const guideNpcIds = new Set(npcs.map((n) => n.monsterId).filter((v): v is number => v !== undefined))
  w.monsters.forEach((m) => {
    const isNpc = monsterKind(w, m) === 'npc'
    // Con o sin foto, toda entidad del catálogo tiene resultado. NPC de las guías: ya tienen su propia ficha (no se duplican).
    if (isNpc && guideNpcIds.has(m.id)) return
    const identity = npcIdentity(m.id)
    // Unificado con otro NPC por el staff (p. ej. Pet Trainer 234 → Trainer 226): una sola ficha, sin duplicado.
    if (identity?.mergedInto !== undefined) return
    const title = npcDisplayName(m.id, m.name)
    const mapNames = Array.from(new Set((w.idx.locationsByMonster.get(m.id) ?? []).map((l) => w.maps.get(l.mapId)?.name).filter(Boolean))) as string[]
    docs.push({
      key: `mon-${m.id}`,
      groupKey: `mon-${m.id}`,
      group: isNpc ? 'NPC' : 'Monstruos',
      title,
      typeLabel: isNpc ? `NPC · función no documentada · ID ${m.id}` : m.level ? `Monstruo · nivel ${m.level} · ID ${m.id}` : `Monstruo · ID ${m.id}`,
      context: mapNames.length ? mapNames.slice(0, 3).join(', ') + (mapNames.length > 3 ? '…' : '') : 'Ubicación no documentada',
      path: monsterPath(m.id),
      entity: { type: 'monster', id: m.id },
      fields: [
        field(title, 10, 'Nombre'),
        ...(title !== m.name ? [field(m.name, 9, 'Nombre en el catálogo')] : []),
        ...(identity?.aliases ?? []).map((a) => field(a, 8, 'Alias')),
        ...spanishAliasesFor(m.name).map((n) => field(n, 9, 'Traducción')),
        field(mapNames.join(', '), 3, 'Mapas'),
        field(isNpc ? 'npc personaje donde esta ubicacion' : 'monstruo donde aparece ubicacion', 2, 'Ubicación'),
      ],
    })
  })

  w.spawns.forEach((s) => {
    // Spots de un NPC unificado (234) se presentan con el nombre de la ficha principal (Trainer 226).
    const canonical = canonicalNpcId(s.monsterId)
    const mon = canonical !== s.monsterId ? guideNpcName(canonical) ?? w.monsters.get(canonical)?.name ?? `NPC ${canonical}` : w.monsters.get(s.monsterId)?.name ?? `Monstruo ${s.monsterId}`
    const map = w.maps.get(s.mapId)?.name ?? `Mapa ${s.mapId}`
    docs.push({
      key: `spot-${s.id}`,
      groupKey: `spot-${s.monsterId}-${s.mapId}`,
      group: 'Spots',
      title: `${mon} · ${map}`,
      typeLabel: s.kind === 'invasion' ? 'Aparición de invasión' : 'Spot',
      context: coordLabel(s) ?? 'Coordenadas no documentadas',
      path: spotPath(s.id),
      entity: { type: 'monster', id: canonical },
      fields: [field(mon, 7, 'Monstruo'), field(map, 6, 'Mapa'), field(s.kind === 'invasion' ? 'invasion aparicion' : 'spot spots aparicion', 3, 'Tipo')],
    })
  })

  w.items.forEach((it) => {
    const altNames = (w.idx.variantsByItem.get(it.id) ?? []).filter((v) => v.id === it.id && v.name !== it.name).map((v) => v.name)
    const skill = skillNameOf(it)
    docs.push({
      key: `item-${it.id}`,
      groupKey: `item-${it.id}`,
      group: 'Ítems',
      title: it.name,
      typeLabel: skill ? `${skillKindOf(it)} · enseña ${skill}` : 'Ítem · datos del servidor',
      context: it.hasVerifiedRoute ? 'Con ruta de obtención verificada' : 'Sin ruta de obtención verificada',
      path: `${itemPath(it.id)}#donde-conseguirlo`,
      entity: { type: 'item', id: it.id },
      fields: [
        field(it.name, 10, 'Nombre'),
        ...(skill
          ? [
              field(skill, 10, 'Poder que enseña'),
              ...spanishAliasesFor(skill).map((n) => field(n, 9, 'Poder (traducción)')),
              field('pergamino libro orbe habilidad poder skill scroll', 2, 'Tipo'),
            ]
          : []),
        ...altNames.map((n) => field(n, 9, 'Nombre alternativo')),
        ...spanishAliasesFor(it.name).map((n) => field(n, 9, 'Traducción')),
        field('item objeto donde conseguir drop', 2, 'Tipo'),
      ],
    })
  })
  w.variants.forEach((v) => {
    if (v.id === v.itemId) return
    docs.push({
      key: `variant-${v.id}`,
      groupKey: `variant-${v.id}`,
      group: 'Ítems',
      title: v.name,
      typeLabel: 'Variante de ítem',
      context: v.nameContext ?? w.items.get(v.itemId)?.name,
      path: itemPath(v.id),
      entity: { type: 'item', id: v.id },
      fields: [
        field(v.name, 10, 'Nombre'),
        ...(v.aliases ?? []).map((n) => field(n, 9, 'Nombre alternativo')),
        ...spanishAliasesFor(v.name).map((n) => field(n, 9, 'Traducción')),
        field('item objeto caja', 2, 'Tipo'),
      ],
    })
  })

  // NPC con la ubicación resuelta con los datos (misma lógica que su tarjeta).
  npcs.forEach((n) => {
    const shop = shopOf(n.id)
    const sold = shop ? publishedShopItems(w, shop).map((it) => itemName(w, it.itemId)) : []
    docs.push(npcDoc(n, npcLocationText(resolveNpcLocation(n, { status: 'ready', data: w })), sold))
  })

  return docs
}
